import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { X, User, Crown, HelpCircle, Home, Edit, ExternalLink, Info, LogOut, ArrowLeft, Upload, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import PricingModal from "@/components/subscription/PricingModal";
interface ProfilePopupProps {
  children: React.ReactNode;
  className?: string;
}
const ProfilePopup = ({
  children,
  className
}: ProfilePopupProps) => {
  const {
    toast
  } = useToast();
  const {
    user,
    signOut
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("account");
  const [isEditMode, setIsEditMode] = useState(false);
  const [name, setName] = useState("");
  const [editName, setEditName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const {
    subscription,
    loading,
    isFetching,
    creditsData,
    createCheckout,
    openCustomerPortal
  } = useSubscription();

  // Load profile data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadProfileData();
    }
  }, [isOpen, user]);
  const loadProfileData = async () => {
    if (!user) return;
    try {
      const {
        data: profile,
        error
      } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
      if (profile) {
        setName(profile.full_name || "");
        setEditName(profile.full_name || "");
        setAvatarUrl(profile.avatar_url);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };
  const handleClose = () => {
    setIsOpen(false);
    setIsEditMode(false);
  };
  const handleEditMode = () => {
    setIsEditMode(true);
    setEditName(name);
  };
  const handleBackToProfile = async () => {
    if (!user) return;

    // Auto-save changes when going back
    try {
      const {
        error
      } = await supabase.from('profiles').upsert({
        id: user.id,
        full_name: editName,
        updated_at: new Date().toISOString()
      });
      if (error) throw error;
      setName(editName);
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated."
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
    setIsEditMode(false);
  };
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      const {
        error: uploadError
      } = await supabase.storage.from('avatars').upload(filePath, file);
      if (uploadError) throw uploadError;

      // Get public URL
      const {
        data
      } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = data.publicUrl;

      // Update profile with new avatar URL
      const {
        error: updateError
      } = await supabase.from('profiles').update({
        avatar_url: publicUrl
      }).eq('id', user.id);
      if (updateError) throw updateError;
      setAvatarUrl(publicUrl);
      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully."
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Upload failed",
        description: "Failed to update profile picture. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleAvatarDelete = async () => {
    if (!user) return;
    try {
      // Update profile to remove avatar URL
      const {
        error
      } = await supabase.from('profiles').update({
        avatar_url: null
      }).eq('id', user.id);
      if (error) throw error;
      setAvatarUrl(null);
      toast({
        title: "Avatar removed",
        description: "Your profile picture has been removed."
      });
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        title: "Error",
        description: "Failed to remove profile picture. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleSaveProfile = async () => {
    if (!user) return;
    try {
      const {
        error
      } = await supabase.from('profiles').upsert({
        id: user.id,
        full_name: editName,
        updated_at: new Date().toISOString()
      });
      if (error) throw error;
      setName(editName);
      setIsEditMode(false);
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated."
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
  };
  const getUserInitials = () => {
    if (name) {
      const names = name.split(' ');
      return names.length >= 2 ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase() : names[0][0].toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "U";
  };
  const handleUpgrade = () => {
    setShowPricingModal(true);
  };

  const handleAddPaymentMethod = async () => {
    const origin = window.location.origin;
    const flowData = {
      type: 'payment_method_update',
      after_completion: {
        type: 'redirect',
        redirect: {
          return_url: `${origin}/`
        }
      }
    };

    await openCustomerPortal(flowData);
  };
  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };
  const getCreditsTotal = () => {
    if (subscription?.subscription_tier === 'Premium') return 3100;
    if (subscription?.subscription_tier === 'Plus') return 1500;
    return 770; // Trial/Free
  };

  // Helper function to calculate days remaining in trial
  const getTrialDaysLeft = () => {
    if (!subscription?.trial_end_date) return 0;
    const trialEnd = new Date(subscription.trial_end_date);
    const now = new Date();
    return Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  };

  // Helper function to format date nicely
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const creditsLeft = creditsData?.credits_left || 0;

  // Calculate actual credits breakdown - for now use simplified logic
  // In a real implementation, gift credits would come from the backend
  const baseCredits = creditsLeft;
  const giftCredits = 0; // Will be populated when backend provides this data
  const getPlanName = () => {
    if (subscription?.subscription_tier) return subscription.subscription_tier;
    return loading ? 'New' : 'Free';
  };
  const menuItems = [{
    id: "account",
    label: "Account",
    icon: User,
    active: true
  },
  /*{
  id: "subscription",
  label: "Subscription",
  icon: Crown,
  active: false
  },*/
  {
    id: "help",
    label: "Get help",
    icon: HelpCircle,
    active: false,
    external: true
  }, {
    id: "homepage",
    label: "Homepage",
    icon: Home,
    active: false,
    external: true
  }];
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
    <DialogTrigger asChild>
      <div className={className}>
        {children}
      </div>
    </DialogTrigger>
    <DialogContent className="max-w-4xl p-0 bg-[#F8F8F7] border border-gray-200 shadow-xl rounded-3xl overflow-hidden" hideCloseButton>
      <div className="flex h-[600px] rounded-3xl overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-[250px] bg-[#F8F8F7] border-r border-gray-200">
          {/* Logo */}
          <div className="p-6">
            <img src="/lovable-uploads/b7d9bd96-8bb9-4c6f-9451-de3bce37cee9.png" alt="Studibudi" className="h-6 w-auto" />
          </div>

          {/* Navigation Menu */}
          <div className="p-2">
            {menuItems.map(item => <button key={item.id} className={`w-full flex items-center justify-between px-3 py-2 rounded-2xl text-left mb-1 transition-colors ${item.id === activeSection && item.active ? 'bg-[#EDEDEB] text-gray-900' : 'hover:bg-[#EDEDEB] text-gray-700'}`} onClick={() => {
              if (item.external) {
                // Handle external links
                if (item.id === 'homepage') {
                  window.location.href = '/';
                } else if (item.id === 'help') {
                  // Open help/support
                }
              } else {
                setActiveSection(item.id);
              }
            }}>
              <div className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </div>
              {item.external && <ExternalLink className="w-3 h-3" />}
            </button>)}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {isEditMode && <Button variant="ghost" size="sm" onClick={handleBackToProfile} className="h-8 w-8 p-0 hover:bg-gray-100">
                <ArrowLeft className="h-5 w-5" />
              </Button>}
              <h1 className="text-xl font-semibold text-gray-900">
                {isEditMode ? "Account" : "Profile"}
              </h1>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 p-4 space-y-8">
            {/* Profile Section */}
            {!isEditMode ? <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 rounded-2xl border border-[#DADAD9]">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
                <AvatarFallback className="bg-[#EDEDEB] text-black text-lg font-medium rounded-2xl">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      {name || user?.email || "User"}
                    </h2>
                    <p className="text-sm text-gray-600">{user?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleEditMode} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleSignOut} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div> : <div className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="w-20 h-20 rounded-2xl border border-[#DADAD9]">
                    {avatarUrl && <AvatarImage src={avatarUrl} alt={editName} />}
                    <AvatarFallback className="bg-[#EDEDEB] text-black text-lg font-medium rounded-2xl">
                      {editName ? editName.charAt(0).toUpperCase() : getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 flex gap-1">
                    <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" id="avatar-upload" />
                    <Button size="sm" variant="secondary" className="h-8 w-8 p-0 rounded-lg bg-white border border-gray-200 hover:bg-gray-50" onClick={() => document.getElementById('avatar-upload')?.click()}>
                      <Upload className="h-3 w-3" />
                    </Button>
                    {avatarUrl && <Button size="sm" variant="secondary" className="h-8 w-8 p-0 rounded-lg bg-white border border-gray-200 hover:bg-gray-50" onClick={handleAvatarDelete}>
                      <Trash2 className="h-3 w-3" />
                    </Button>}
                  </div>
                </div>
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Name</label>
                <div className="relative">
                  <Input value={editName} onChange={e => setEditName(e.target.value)} className="pr-8 rounded-xl border-gray-200" placeholder="Enter your name" />
                  {editName && <Button variant="ghost" size="sm" onClick={() => setEditName("")} className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </Button>}
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input value={user?.email || ""} disabled className="rounded-xl border-gray-200 bg-gray-50 text-gray-500" />
              </div>

              {/* Save Button */}
              <div className="pt-6">
                <Button
                  onClick={handleSaveProfile}
                  className="w-full rounded-xl"
                >
                  Save Changes
                </Button>
              </div>
            </div>}

            {/* Subscription Section - Only show in profile view mode */}
            {!isEditMode && <div className="bg-[#F0F0EF] p-4 rounded-2xl space-y-3">
              {/* Plan Section */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {/* Plan Title */}
                  <div className="text-2xl font-semibold text-gray-900">
                    {(() => {
                      if (!subscription || loading) return 'Loading...';

                      // Inactive States
                      if (subscription?.subscription_status === 'past_due' ||
                        subscription?.subscription_status === 'expired' ||
                        (subscription?.subscription_status === 'canceled' &&
                          subscription?.subscription_end_date &&
                          new Date(subscription.subscription_end_date) < new Date())) {
                        return 'Inactive';
                      }

                      // Trial States
                      if (subscription?.subscription_status === 'trialing') {
                        const isAnnual = subscription?.subscription_tier?.includes('Annual');
                        const baseTier = subscription?.subscription_tier?.replace(' Annual', '') || 'Plus';
                        return isAnnual ? `${baseTier} Annual` : baseTier;
                      }

                      // Canceled (still active)
                      if (subscription?.subscription_status === 'canceled' &&
                        subscription?.subscription_end_date &&
                        new Date(subscription.subscription_end_date) >= new Date()) {
                        const baseTier = subscription?.subscription_tier?.replace(' Annual', '') || 'Plus';
                        return `${baseTier} (Canceled)`;
                      }

                      // Active subscriptions
                      if (subscription?.subscribed) {
                        const isAnnual = subscription?.subscription_tier?.includes('Annual');
                        const baseTier = subscription?.subscription_tier?.replace(' Annual', '') || 'Plus';
                        return isAnnual ? `${baseTier} Annual` : baseTier;
                      }

                      return subscription?.subscription_tier || 'Free';
                    })()}
                  </div>

                  {/* Subtitle */}
                  <div className="mt-1">
                    {(() => {
                      if (!subscription || loading) return null;

                      // Inactive States
                      if (subscription?.subscription_status === 'past_due' ||
                        subscription?.subscription_status === 'expired' ||
                        (subscription?.subscription_status === 'canceled' &&
                          subscription?.subscription_end_date &&
                          new Date(subscription.subscription_end_date) < new Date())) {
                        return <p className="text-sm text-muted-foreground">No active subscription</p>;
                      }

                      // Trial States
                      if (subscription?.subscription_status === 'trialing' && subscription?.trial_end_date) {
                        const daysLeft = getTrialDaysLeft();

                        if (daysLeft <= 3 && daysLeft > 0) {
                          return <p className="text-sm text-orange-600 font-medium">Trial: {daysLeft} day{daysLeft === 1 ? '' : 's'} left</p>;
                        } else if (daysLeft > 0) {
                          return <p className="text-sm text-muted-foreground">Trial: {daysLeft} days remaining</p>;
                        } else {
                          return <p className="text-sm text-red-600">Trial expired</p>;
                        }
                      }

                      // Canceled (still active)
                      if (subscription?.subscription_status === 'canceled' &&
                        subscription?.subscription_end_date &&
                        new Date(subscription.subscription_end_date) >= new Date()) {
                        return <p className="text-sm text-muted-foreground">Canceled - Ends {formatDate(subscription.subscription_end_date)}</p>;
                      }

                      // Active subscriptions
                      if (subscription?.subscribed && subscription?.subscription_end_date) {
                        return <p className="text-sm text-muted-foreground">Renews {formatDate(subscription.subscription_end_date)}</p>;
                      }

                      return null;
                    })()}
                  </div>

                  {/* Additional Info/Warnings */}
                  <div className="mt-2">
                    {(() => {
                      if (!subscription || loading) return null;

                      // Trial warning (days 13-15 without payment)
                      if (subscription?.subscription_status === 'trialing' &&
                        !subscription?.payment_method_added &&
                        subscription?.trial_end_date) {
                        const daysLeft = getTrialDaysLeft();
                        if (daysLeft <= 3 && daysLeft > 0) {
                          return (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                              <p className="text-sm text-orange-700 font-medium">⚠ Trial expiring soon! Add payment method now to continue</p>
                            </div>
                          );
                        }
                      }

                      // Trial with payment method - auto-renew info
                      if (subscription?.subscription_status === 'trialing' &&
                        subscription?.payment_method_added &&
                        subscription?.trial_end_date) {
                        return <p className="text-sm text-green-600">Auto-renews on {formatDate(subscription.trial_end_date)}</p>;
                      }

                      // Canceled warning
                      if (subscription?.subscription_status === 'canceled' &&
                        subscription?.subscription_end_date &&
                        new Date(subscription.subscription_end_date) >= new Date()) {
                        return <p className="text-sm text-orange-600">Access until {formatDate(subscription.subscription_end_date)}</p>;
                      }

                      // Out of credits alert
                      if (subscription?.subscribed && creditsLeft === 0 && subscription?.subscription_end_date) {
                        return <p className="text-sm text-destructive">Out of credits - Refreshes on {formatDate(subscription.subscription_end_date)}</p>;
                      }

                      // Inactive warning
                      if (subscription?.subscription_status === 'past_due' ||
                        subscription?.subscription_status === 'expired' ||
                        (subscription?.subscription_status === 'canceled' &&
                          subscription?.subscription_end_date &&
                          new Date(subscription.subscription_end_date) < new Date())) {
                        return <p className="text-sm text-destructive font-medium">Subscription Inactive - Reactivate to continue</p>;
                      }

                      // Annual savings badge
                      if (subscription?.subscribed && subscription?.subscription_tier?.includes('Annual')) {
                        return (
                          <div className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full mt-1">
                            20% Annual Savings
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 ml-4">
                  {(() => {
                    if (!subscription || loading) {
                      return <Button disabled variant="outline" className="h-9 px-4">Loading...</Button>;
                    }

                    // Inactive States - Show Reactivate Button
                    if (subscription?.subscription_status === 'past_due' ||
                      subscription?.subscription_status === 'expired' ||
                      (subscription?.subscription_status === 'canceled' &&
                        subscription?.subscription_end_date &&
                        new Date(subscription.subscription_end_date) < new Date())) {
                      return (
                        <Button
                          onClick={handleUpgrade}
                          variant="destructive"
                          className="h-9 px-4"
                        >
                          Reactivate Plan
                        </Button>
                      );
                    }

                    // Trial without payment method
                    if (subscription?.subscription_status === 'trialing' && !subscription?.payment_method_added) {
                      const daysLeft = getTrialDaysLeft();

                      if (daysLeft <= 3 && daysLeft > 0) {
                        return (
                          <Button
                            onClick={handleAddPaymentMethod}
                            className="h-9 px-4 bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                          >
                            ⚠ Add Payment Method
                          </Button>
                        );
                      } else {
                        return (
                          <Button
                            onClick={handleAddPaymentMethod}
                            variant="default"
                            className="h-9 px-4 rounded-2xl"
                          >
                            Add Payment Method
                          </Button>
                        );
                      }
                    }

                    // Trial with payment method OR Active subscription
                    if ((subscription?.subscription_status === 'trialing' && subscription?.payment_method_added) ||
                      subscription?.subscribed) {
                      const buttons = [];

                      // Primary button - Manage Plan
                      buttons.push(
                        <Button
                          key="manage"
                          onClick={async () => {
                            try {
                              await openCustomerPortal();
                            } catch (error) {
                              console.error('Customer portal error:', error);
                              // If customer portal fails, try to refresh the page and retry
                              toast({
                                title: "Retrying...",
                                description: "Refreshing authentication and trying again.",
                              });

                              // Refresh the page to clear any stale auth state
                              window.location.reload();
                            }
                          }}
                          variant="outline"
                          className="h-9 px-4 rounded-2xl"
                        >
                          Manage Plan
                        </Button>
                      );

                      // Secondary button - Change plan (only for Plus users)
                      if (subscription?.subscription_tier?.includes('Plus')) {
                        buttons.push(
                          <Button
                            key="upgrade"
                            onClick={() => setShowPricingModal(true)}
                            variant="secondary"
                            className="h-9 px-4 text-sm"
                          >
                            Change plan
                          </Button>
                        );
                      }

                      return <div className="flex flex-col gap-2">{buttons}</div>;
                    }

                    // Canceled (still active)
                    if (subscription?.subscription_status === 'canceled' &&
                      subscription?.subscription_end_date &&
                      new Date(subscription.subscription_end_date) >= new Date()) {
                      return (
                        <Button
                          onClick={openCustomerPortal}
                          variant="default"
                          className="h-9 px-4"
                        >
                          Reactivate
                        </Button>
                      );
                    }

                    // Default - Subscribe
                    return (
                      <Button
                        onClick={handleUpgrade}
                        variant="default"
                        className="h-9 px-4"
                      >
                        Subscribe
                      </Button>
                    );
                  })()}
                </div>
              </div>

              <hr className="border-dashed border-gray-300" />

              {/* Credits Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/lovable-uploads/b6a095b1-b91d-4af2-9e04-0842c85ba69b.png" alt="Credits" className="w-5 h-5" />
                    <h3 className="text-lg font-medium text-gray-900">Credits</h3>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className={`text-lg font-semibold ${creditsLeft === 0 ? 'text-destructive' : 'text-foreground'}`}>
                    {loading ? '--' : creditsLeft}
                  </div>
                </div>

                {!loading && creditsData && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Credits</span>
                      <span className="text-sm text-foreground">{baseCredits}</span>
                    </div>
                    {giftCredits > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Gift</span>
                        <span className="text-sm text-foreground">{giftCredits}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>}
          </div>
        </div>
      </div>
    </DialogContent>


    {/* Pricing Modal */}
    <PricingModal
      open={showPricingModal}
      onOpenChange={setShowPricingModal}
      currentSubscription={subscription}
    />
  </Dialog>;
};
export default ProfilePopup;